import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, callName, type CurrentUser } from '@pst/shared';
import { ApiError, api } from '../api';
import { PasskeysSection } from './PasskeysSection';

/**
 * One's own name. The Anzeigename is what PST calls someone in the header
 * and greetings; left empty, the first name is used.
 */
export function ProfileModal({ user, opened, onClose }: { user: CurrentUser; opened: boolean; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.displayName);
  const [nickname, setNickname] = useState(user.nickname ?? '');
  const [codes, setCodes] = useState(user.ownerCodes.join(', '));
  useEffect(() => {
    if (opened) {
      setName(user.displayName);
      setNickname(user.nickname ?? '');
      setCodes(user.ownerCodes.join(', '));
    }
  }, [opened, user]);

  const save = useMutation({
    mutationFn: () => api.updateProfile({ displayName: name.trim(), nickname: nickname.trim() || null, ownerCodes: codes.trim() || null }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['me'], fresh);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Profil" size="md">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <Stack gap="sm">
          {save.isError && <Alert color="red">{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
          <TextInput label="Name" required value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          <TextInput
            label="Anzeigename"
            placeholder={callName(name, null)}
            value={nickname}
            onChange={(e) => setNickname(e.currentTarget.value)}
          />
          <Text size="xs" c="dimmed">
            So nennt PST Sie oben rechts. Leer lassen für den Vornamen ({callName(name, null)}).
          </Text>
          <TextInput
            label="Meine Kürzel im Terminplan"
            placeholder="z. B. A"
            description="Wie Sie in der Spalte „Verantwortlich“ stehen, mehrere mit Komma. Daraus entstehen „Meine Aufgaben“ und Ihr Kalender."
            value={codes}
            onChange={(e) => setCodes(e.currentTarget.value)}
          />
          <TextInput label="E-Mail-Adresse" value={user.email} readOnly description="Dient als Anmeldename; ändern kann sie nur die Administration." />
          <TextInput label="Rolle" value={ROLE_LABELS[user.role]} readOnly />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>Speichern</Button>
          </Group>
        </Stack>
      </form>
      <Stack mt="md">
        <PasskeysSection />
      </Stack>
    </Modal>
  );
}
